import { Link } from "react-router-dom";
import { formatKabulDisplay } from "./ScheduleFields";
import { FilterToolbar } from "./FilterToolbar";
import { LtrIsolate } from "./LtrIsolate";
import type { StatusTone } from "../lib/overview-ops";
import {
  SPONSORED_FEATURE_NOTE,
  relatedEntitlementsLinks,
  sourceDisplay,
  type EntitlementInventoryRow,
  type EntitlementSortKey,
  type EntitlementStatusLike,
} from "../lib/entitlements-ops";

const TONE_BADGE: Record<StatusTone, string> = {
  success: "state-badge--published",
  warning: "state-badge--paused",
  danger: "state-badge--emergency",
  info: "state-badge--scheduled",
  neutral: "state-badge--archived",
};

type FilterBarProps = {
  filter: string;
  onFilterChange: (value: string) => void;
  query: string;
  onQueryChange: (value: string) => void;
  sort: EntitlementSortKey;
  onSortChange: (value: EntitlementSortKey) => void;
  organizationLabel: string;
  planLabel: string;
  planCode: string;
};

export function EntitlementsFilterBar({
  filter,
  onFilterChange,
  query,
  onQueryChange,
  sort,
  onSortChange,
  organizationLabel,
  planLabel,
  planCode,
}: FilterBarProps) {
  const chips: Array<{ id: string; label: string }> = [
    { id: "all", label: "همه" },
    { id: "features", label: "امکانات" },
    { id: "limits", label: "سقف‌ها" },
    { id: "allowed", label: "مجاز" },
    { id: "denied", label: "غیرمجاز" },
    { id: "inherited", label: "پیش‌فرض طرح" },
    { id: "overridden", label: "تغییر سازمان" },
    { id: "sponsored", label: "تبلیغات ویژه" },
  ];
  return (
    <FilterToolbar
      className="list-filter-bar announcements-filter-bar"
      aria-label="فیلتر دسترسی پلان"
    >
      <p className="sites-inventory__muted" style={{ width: "100%" }}>
        سازمان فعال:{" "}
        <strong>
          <LtrIsolate mono>{organizationLabel}</LtrIsolate>
        </strong>
        {" · طرح: "}
        <strong>{planLabel}</strong>{" "}
        (
        <LtrIsolate mono>{planCode}</LtrIsolate>
        )
      </p>
      <div
        className="filter-toolbar__chips announcements-filter-bar__chips"
        role="group"
        aria-label="فیلتر دسترسی پلان"
      >
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`btn btn--sm ${filter === c.id ? "btn--primary" : "btn--ghost"}`}
            onClick={() => onFilterChange(c.id)}
            aria-pressed={filter === c.id}
          >
            {c.label}
          </button>
        ))}
      </div>
      <label className="announcements-filter-bar__field">
        <span className="visually-hidden">جستجو</span>
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="امکان، سقف، شناسه فنی…"
          aria-label="جستجوی دسترسی پلان"
        />
      </label>
      <label className="announcements-filter-bar__field">
        <span>مرتب‌سازی</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as EntitlementSortKey)}
          aria-label="مرتب‌سازی فهرست دسترسی پلان"
        >
          <option value="label">نام</option>
          <option value="kind">نوع</option>
          <option value="access">وضعیت</option>
          <option value="source">منبع</option>
        </select>
      </label>
    </FilterToolbar>
  );
}

export function EntitlementInspectSummary({
  item,
  status,
}: {
  item: EntitlementInventoryRow;
  status: EntitlementStatusLike;
}) {
  const source = sourceDisplay(item.source === "org_override");
  const links = relatedEntitlementsLinks();

  return (
    <section className="content-inspect" aria-labelledby="entitlement-inspect-title">
      <header className="content-inspect__header">
        <div className="content-inspect__identity">
          <p className="content-inspect__kind">
            {item.kind === "feature" ? "امکان طرح" : "سقف مصرف"}
          </p>
          <h2 id="entitlement-inspect-title" className="content-inspect__name">
            {item.labelFa}
          </h2>
          <p className="content-inspect__id">
            شناسه فنی <LtrIsolate mono>{item.technicalId}</LtrIsolate>
          </p>
        </div>
        <div className="content-inspect__badges" aria-label="وضعیت و منبع">
          <span className={`state-badge ${TONE_BADGE[item.accessTone]}`}>{item.accessLabel}</span>
          <span className={`state-badge ${TONE_BADGE[source.tone]}`}>{item.sourceLabel}</span>
          {item.isSponsored && (
            <span className="state-badge state-badge--scheduled">تبلیغات ویژه</span>
          )}
        </div>
      </header>

      {item.isSponsored && (
        <p className="content-inspect__eligibility-detail">{SPONSORED_FEATURE_NOTE}</p>
      )}
      {source.detail && <p className="content-inspect__eligibility-detail">{source.detail}</p>}

      <dl className="content-inspect__facts">
        <div>
          <dt>سازمان</dt>
          <dd>
            <LtrIsolate mono>{status.organizationId}</LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>طرح</dt>
          <dd>
            {status.planDisplayName}{" "}
            (
            <LtrIsolate mono>{status.planCode}</LtrIsolate>
            )
          </dd>
        </div>
        <div>
          <dt>منبع مقدار</dt>
          <dd>{item.sourceLabel}</dd>
        </div>
        {item.kind === "feature" ? (
          <div>
            <dt>دسترسی مؤثر</dt>
            <dd>{item.accessLabel}</dd>
          </div>
        ) : (
          <>
            <div>
              <dt>مصرف</dt>
              <dd>
                <LtrIsolate mono>{item.usageText}</LtrIsolate>
              </dd>
            </div>
            <div>
              <dt>سقف</dt>
              <dd>
                <LtrIsolate mono>{item.maxText}</LtrIsolate>
              </dd>
            </div>
            <div>
              <dt>وضعیت مصرف</dt>
              <dd>{item.accessLabel}</dd>
            </div>
          </>
        )}
        <div>
          <dt>تخصیص</dt>
          <dd>
            <LtrIsolate mono>{formatKabulDisplay(status.assignedAt)}</LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>به‌روزرسانی</dt>
          <dd>
            <LtrIsolate mono>{formatKabulDisplay(status.updatedAt)}</LtrIsolate>
          </dd>
        </div>
        {status.notes && (
          <div>
            <dt>یادداشت</dt>
            <dd>{status.notes}</dd>
          </div>
        )}
      </dl>

      <nav className="content-inspect__links" aria-label="مسیرهای مرتبط">
        {links.map((link) => (
          <Link key={link.id} to={link.to} className="btn btn--ghost btn--sm">
            {link.label}
          </Link>
        ))}
      </nav>
    </section>
  );
}

export function EntitlementsInventoryList({
  rows,
  selectedId,
  onSelect,
}: {
  rows: EntitlementInventoryRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <>
      <div
        className="sites-inventory-table-wrap inventory-table-wrap"
        role="region"
        aria-label="جدول دسترسی پلان"
      >
        <table className="data-table content-inventory-table inventory-table">
          <thead>
            <tr>
              <th scope="col">مورد</th>
              <th scope="col">نوع</th>
              <th scope="col">وضعیت</th>
              <th scope="col">منبع</th>
              <th scope="col">مصرف / سقف</th>
              <th scope="col">
                <span className="visually-hidden">جزئیات</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = row.id === selectedId;
              return (
                <tr
                  key={row.id}
                  className={selected ? "sites-inventory-table__row--selected" : undefined}
                >
                  <td>
                    <button
                      type="button"
                      className="sites-inventory__select-btn"
                      onClick={() => onSelect(row.id)}
                      aria-pressed={selected}
                    >
                      {row.labelFa}
                      {row.isSponsored ? " (تبلیغات ویژه)" : ""}
                    </button>
                    <div className="sites-inventory__muted">
                      <LtrIsolate mono>{row.technicalId}</LtrIsolate>
                    </div>
                  </td>
                  <td>{row.kind === "feature" ? "امکان" : "سقف"}</td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[row.accessTone]}`}>
                      {row.accessLabel}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`state-badge ${TONE_BADGE[sourceDisplay(row.source === "org_override").tone]}`}
                    >
                      {row.sourceLabel}
                    </span>
                  </td>
                  <td>
                    {row.kind === "limit" ? (
                      <LtrIsolate mono>
                        {row.usageText} / {row.maxText}
                      </LtrIsolate>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => onSelect(row.id)}
                    >
                      جزئیات
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul
        className="sites-inventory-cards content-inventory-cards inventory-cards"
        aria-label="کارت‌های دسترسی پلان"
      >
        {rows.map((row) => {
          const selected = row.id === selectedId;
          return (
            <li
              key={row.id}
              className={`sites-inventory-card${selected ? " sites-inventory-card--selected" : ""}`}
            >
              <button
                type="button"
                className="sites-inventory-card__hit"
                onClick={() => onSelect(row.id)}
                aria-pressed={selected}
              >
                <span className="sites-inventory-card__title">
                  {row.labelFa}
                  {row.isSponsored ? " · تبلیغات ویژه" : ""}
                </span>
                <span className="sites-inventory-card__badges">
                  <span className={`state-badge ${TONE_BADGE[row.accessTone]}`}>
                    {row.accessLabel}
                  </span>
                  <span
                    className={`state-badge ${TONE_BADGE[sourceDisplay(row.source === "org_override").tone]}`}
                  >
                    {row.sourceLabel}
                  </span>
                </span>
                <span className="sites-inventory__muted">
                  <LtrIsolate mono>{row.technicalId}</LtrIsolate>
                  {row.kind === "limit" && (
                    <>
                      {" · "}
                      <LtrIsolate mono>
                        {row.usageText} / {row.maxText}
                      </LtrIsolate>
                    </>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
}

export function PlanSummaryBanner({
  status,
  organizationLabel,
}: {
  status: EntitlementStatusLike;
  organizationLabel: string;
}) {
  const sponsored = status.features.sponsored;
  return (
    <div className="content-inspect" style={{ marginBottom: "1rem" }}>
      <dl className="content-inspect__facts">
        <div>
          <dt>سازمان فعال</dt>
          <dd>
            <LtrIsolate mono>{organizationLabel}</LtrIsolate>
          </dd>
        </div>
        <div>
          <dt>طرح تخصیص‌یافته</dt>
          <dd>
            {status.planDisplayName}{" "}
            (
            <LtrIsolate mono>{status.planCode}</LtrIsolate>
            )
          </dd>
        </div>
        <div>
          <dt>تبلیغات ویژه</dt>
          <dd>
            <span
              className={`state-badge ${sponsored ? "state-badge--published" : "state-badge--emergency"}`}
            >
              {sponsored ? "مجاز" : "غیرمجاز"}
            </span>
          </dd>
        </div>
      </dl>
    </div>
  );
}
